import { useEffect, useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Card,
  CardContent,
  List,
  ListItemText,
  TextField,
  Typography,
  InputAdornment,
  ListItem,
} from '@mui/material';
import NotesIcon from '@mui/icons-material/Notes';
import { styled } from '@mui/material/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { SyncedTripLeg, TripLeg } from '../../../data/models/reservation.model';
import * as accountingSlice from '../../../data/accounting.slice';
import * as lookupSlice from '../../../data/lookup.slice';
import * as tripLegSlice from '../../../data/tripleg.slice';
import * as providerSlice from '../../../data/provider.slice';
import {
  TripLegPassengerNoteType,
  TripLegDriverNoteType,
  TripLegProviderNoteType,
} from '../../../data/models/note.model';
import reservationUtils from '../../../services/reservationUtils';
import { useAppSelector } from '../../../hooks/useAppSelector';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { useCommonTranslation } from '../../../hooks/useCommonTranslation';
import { Send } from '@mui/icons-material';
import { DateTime } from 'luxon';
import { useIsSuperReaderUser } from '../../../hooks/permissions/useIsSuperReaderUser';
import { useIsPassengerPortalOnlyUser } from '../../../hooks/permissions/useIsPassengerPortalOnlyUser';

const PREFIX = 'TripLegActivity';

enum AccordionName {
  PassengerNotes = 'PassengerNotes',
  DriverNotes = 'DriverNotes',
  InternalNotes = 'InternalNotes',
}

const classes = {
  container: `${PREFIX}-container`,
  removeBackground: `${PREFIX}-removeBackground`,
  containerHeader: `${PREFIX}-containerHeader`,
  searchBox: `${PREFIX}-searchBox`,
  addTaskButton: `${PREFIX}-addTaskButton`,
  typography: `${PREFIX}-typography`,
  timeText: `${PREFIX}-timeText`,
  sendBtn: `${PREFIX}-sendBtn`,
  noteContent: `${PREFIX}-noteContent`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`&.${classes.container}`]: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'stretch',
    [`& .${classes.removeBackground}::before`]: {
      background: 'none',
    },
    [`& .${classes.removeBackground}.Mui-expanded`]: {
      borderTop: 'none',
    },
  },
  [`& .${classes.containerHeader}`]: {
    display: 'flex',
    justifyContent: 'space-between',
    paddingBottom: theme.spacing(1),
  },
  [`& .${classes.searchBox}`]: {
    marginBottom: theme.spacing(2),
    marginRight: theme.spacing(2),
    width: '40%',
  },
  [`& .${classes.addTaskButton}`]: {
    marginRight: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
  },

  [`& .${classes.typography}`]: {
    fontFamily: 'Source Sans Pro',
    fontSize: '14px',
    fontStyle: 'normal',
    fontWeight: '400',
    lineHeight: '143%',
    letterSpacing: '0.17px',
    wordWrap: 'break-word',
  },

  [`& .${classes.timeText}`]: {
    paddingLeft: theme.spacing(2),
  },

  [`& .${classes.sendBtn}`]: {
    cursor: 'pointer',
  },

  [`& .${classes.noteContent}`]: {
    wordBreak: 'break-word',
  },
}));

const TripLegActivity = ({
  tripLeg,
  syncedTripLeg,
}: {
  tripLeg: TripLeg;
  syncedTripLeg: SyncedTripLeg | null;
}) => {
  const t = useCommonTranslation();
  const dispatch = useAppDispatch();
  const isSuperReaderUser = useIsSuperReaderUser();
  const isPassengerPortalOnlyUser = useIsPassengerPortalOnlyUser();
  const [internalNotes, setInternalNotes] = useState<Record<string, string>>(
    {},
  );
  const [isValidNote, setIsValidNote] = useState(true);
  const [passengerNotes, setPassengerNotes] = useState<
    TripLegPassengerNoteType[]
  >([]);
  const [driverNotes, setDriverNotes] = useState<TripLegDriverNoteType[]>([]);
  const [expanded, setExpanded] = useState<{ [key: string]: AccordionName[] }>(
    {},
  );
  const transportationAttributes = useAppSelector(
    accountingSlice.selectTransportationAttributes,
  );

  const passengerNoteTypes = useAppSelector(
    lookupSlice.selectPassengerNoteTypes,
  );

  const internalNoteList = useAppSelector(providerSlice.selectInternalNoteList);

  const driverNoteTypes = useAppSelector(lookupSlice.selectDriverNoteTypes);

  const timeZones = useAppSelector(lookupSlice.selectTimeZones);

  const tripTimeZone =
    tripLeg?.pickUpAddress?.timeZone || tripLeg?.dropOffAddress?.timeZone;

  const tripTimeZoneAbbreviation = useMemo(() => {
    if (!tripTimeZone || !timeZones?.length) return undefined;
    const tz = timeZones.find((z) => z.ianaStandard === tripTimeZone);
    if (!tz) return tripTimeZone;

    const now = DateTime.now().setZone(tripTimeZone);
    const isDST = now.isInDST;

    if (isDST && tz.timeZoneDayLight) {
      return tz.timeZoneDayLight;
    }
    if (!isDST && tz.timeZoneStandard) {
      return tz.timeZoneStandard;
    }

    return now.offsetNameShort;
  }, [timeZones, tripTimeZone]);

  useEffect(() => {
    dispatch(tripLegSlice.clearTripLegDetails());
  }, []);

  useEffect(() => {
    if (tripLeg?.tripLegReferenceID) {
      dispatch(tripLegSlice.getTripLegDetailsAsync(tripLeg.tripLegReferenceID));
    }
  }, [tripLeg]);

  useEffect(() => {
    const memberDefaults = reservationUtils.getMemberDefaults(
      tripLeg,
      transportationAttributes,
    );
    const selectedPassengerNotes = passengerNoteTypes.filter((note) =>
      memberDefaults?.PassengerNoteTypes.includes(
        note.tripLegPassengerNoteTypeBC,
      ),
    );
    const selectedDriverNotes = driverNoteTypes.filter((note) =>
      memberDefaults?.DriverNoteTypes.includes(note.tripLegDriverNoteTypeBC),
    );
    setDriverNotes(selectedDriverNotes);
    setPassengerNotes(selectedPassengerNotes);
  }, [tripLeg, transportationAttributes]);

  const handleSend = () => {
    const id = syncedTripLeg?.tripLegReferenceID;
    if (!id) {
      return;
    }

    const note = internalNotes[id];
    if (note.length > 200) {
      setIsValidNote(false);
    } else {
      if (tripLeg?.tripLegReferenceID) {
        dispatch(
          providerSlice.addInternalNoteAsync(tripLeg.tripLegReferenceID, note),
        );
        setIsValidNote(true);
        setInternalNotes({ ...internalNotes, [id]: '' });
      }
    }
  };

  const handleAccordionChange =
    (accordion: AccordionName) =>
    (event: React.SyntheticEvent, isExpanded: boolean) => {
      const id = tripLeg?.tripLegReferenceID;
      if (!id) {
        return;
      }
      if (isExpanded) {
        setExpanded({
          ...expanded,
          [id]: [...(expanded[id] ?? []), accordion],
        });
      } else {
        setExpanded({
          ...expanded,
          [id]: expanded[id]?.filter((item) => item !== accordion) ?? [],
        });
      }
    };

  const handleInternalNoteChange = (note: string) => {
    if (syncedTripLeg?.tripLegReferenceID) {
      setInternalNotes({
        ...internalNotes,
        [syncedTripLeg.tripLegReferenceID]: note,
      });
    }
  };

  const isExpanded = (accordionName: AccordionName) => {
    if (tripLeg?.tripLegReferenceID) {
      return (expanded[tripLeg.tripLegReferenceID] ?? []).includes(
        accordionName,
      );
    }
    return false;
  };

  return (
    <StyledBox className={classes.container}>
      <Card>
        <CardContent>
          <Box className={classes.containerHeader}>
            <Typography variant="h6">
              {t('reservation.internalNotes')}
            </Typography>
          </Box>
          {syncedTripLeg &&
            !isSuperReaderUser &&
            !isPassengerPortalOnlyUser && (
              <Box display="flex" alignItems="center" gap={1} my={1}>
                <TextField
                  size="small"
                  error={!isValidNote}
                  fullWidth
                  value={internalNotes[syncedTripLeg.tripLegReferenceID] ?? ''}
                  helperText={!isValidNote ? t('reservation.noteLimit') : ''}
                  onChange={(e) => handleInternalNoteChange(e.target.value)}
                  inputProps={{ maxLength: 200 }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment
                        position="end"
                        onClick={handleSend}
                        className={classes.sendBtn}>
                        <Send />
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
            )}
          <Accordion
            className={classes.removeBackground}
            expanded={isExpanded(AccordionName.PassengerNotes)}
            onChange={handleAccordionChange(AccordionName.PassengerNotes)}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              {t('reservation.passengerNotes')}
            </AccordionSummary>
            <AccordionDetails>
              <List>
                {passengerNotes.map((passengerNote) => (
                  <ListItemText
                    key={passengerNote.tripLegPassengerNoteTypeID}
                    primary={passengerNote.tripLegPassengerNoteTypeName}
                  />
                ))}
              </List>
            </AccordionDetails>
          </Accordion>
          <Accordion
            expanded={isExpanded(AccordionName.DriverNotes)}
            onChange={handleAccordionChange(AccordionName.DriverNotes)}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              {t('reservation.driverNotes')}
            </AccordionSummary>
            <AccordionDetails>
              <List>
                {driverNotes.map((driverNote) => (
                  <ListItemText
                    key={driverNote.tripLegDriverNoteTypeID}
                    primary={driverNote.tripLegDriverNoteTypeName}
                  />
                ))}
              </List>
            </AccordionDetails>
          </Accordion>
          {!isPassengerPortalOnlyUser && syncedTripLeg && (
            <Accordion
              expanded={isExpanded(AccordionName.InternalNotes)}
              onChange={handleAccordionChange(AccordionName.InternalNotes)}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                {t('reservation.internalNotes')}
              </AccordionSummary>
              <AccordionDetails>
                <List>
                  {internalNoteList?.map(
                    (internalNote: TripLegProviderNoteType) => (
                      <ListItem key={internalNote.tripLegInternalNoteID}>
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            {DateTime.fromISO(internalNote.createdOnUTC)
                              .setZone(tripTimeZone || undefined)
                              .toFormat('MM/dd/yy h:mm a')}{' '}
                            {tripTimeZoneAbbreviation &&
                              `(${tripTimeZoneAbbreviation})`}{' '}
                          </Typography>
                          <Typography className={classes.noteContent}>
                            {internalNote.tripLegInternalNoteComment}{' '}
                            {internalNote.createdByUser &&
                              `(${[
                                internalNote.createdByUser.userFirstName,
                                internalNote.createdByUser.userLastName,
                              ]
                                .filter(Boolean)
                                .join(' ')})`}
                          </Typography>
                        </Box>
                      </ListItem>
                    ),
                  )}
                </List>
              </AccordionDetails>
            </Accordion>
          )}
        </CardContent>
      </Card>
    </StyledBox>
  );
};

export default TripLegActivity;
